import { apiClient } from '@/auth';

/**
 * Water consumption — v1 parity for the food-log day header.
 * Endpoints (MNT swagger:5061, 5081):
 *   • GET  /patients/{id}/waters  — date range (used to seed the per-day
 *     value without firing one request per day on the timeline).
 *   • PUT  /patients/{id}/water?date=YYYY-MM-DD&cups=N — set the day's
 *     value. The server treats it as an UPSERT (creates the row when
 *     none exists for the date).
 *
 * `cups` is `number` on the wire (server schema says `type: number`) but
 * always renders as a whole-cup count in the UI — clinicians don't
 * partial-cup count and v1 only ever increments by 1.
 */

export type WaterEntry = {
  id?: number | null;
  water_date: string; // YYYY-MM-DD
  patient_id: number;
  cups: number;
};

export const waterApi = {
  /** Range fetch — typically a month so the per-day widget hits the cache
   *  instead of firing N daily requests across the timeline. */
  async list(patientId: number, dateSince: string, dateUntil: string): Promise<WaterEntry[]> {
    try {
      const res = await apiClient.get<WaterEntry[]>(`/patients/${patientId}/waters`, {
        params: { date_since: dateSince, date_until: dateUntil },
      });
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      // 404 on this endpoint historically means "no water rows in range",
      // not an error — match v1's behavior (waterLog.tsx:34).
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) return [];
      throw err;
    }
  },
  /** UPSERT for a specific day. Returns the persisted row. */
  async set(patientId: number, date: string, cups: number): Promise<WaterEntry | null> {
    const res = await apiClient.put<WaterEntry>(`/patients/${patientId}/water`, undefined, {
      params: { date, cups },
    });
    return res.data ?? null;
  },
};
